import { createTheme, type Theme } from '@mui/material/styles';
import { CONTENT_PALETTE, type AppColorMode } from '../constants/contentColors';
import { APP_FONT_FAMILY_VAR } from '../constants/fonts';

/**
 * Shell theme: sidebar, phone app bar, account menu and anything rendered
 * outside the content tile. Admin red is a flat, solid accent (no gradients,
 * no hover lifts); success is the same green as the content area. Pages render inside
 * `ContentThemeProvider`, which builds its own theme from the content tokens.
 */
export function createAppTheme(mode: AppColorMode): Theme {
  const isDark = mode === 'dark';
  const p = CONTENT_PALETTE[mode];
  const surface = isDark ? '#1E293B' : '#FFFFFF';
  const hairline = isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.08)';

  return createTheme({
    palette: {
      mode,
      primary: { main: '#DC2626', light: '#EF4444', dark: '#B91C1C', contrastText: '#FFFFFF' },
      secondary: { ...p.secondary },
      success: { ...p.success },
      warning: { ...p.warning },
      error: { ...p.error },
      info: { ...p.info },
      background: {
        default: isDark ? '#0F172A' : '#F1F5F9',
        paper: surface,
      },
      text: {
        primary: isDark ? '#F8FAFC' : '#1E293B',
        secondary: isDark ? '#94A3B8' : '#64748B',
      },
      divider: hairline,
    },
    typography: {
      fontFamily: APP_FONT_FAMILY_VAR,
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundColor: surface,
            color: isDark ? '#F8FAFC' : '#1E293B',
            borderBottom: `1px solid ${hairline}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: surface,
            borderColor: hairline,
          },
        },
      },
    },
  });
}
